import { reportPlanOfferDetailsMap } from "@ishvaram/domain-data/report-plan-offer-details";
import { reportPlanSummaryMap } from "@ishvaram/domain-data/report-plan-summaries";
import type { PremiumReportPlanId, ReportDeliverables } from "@ishvaram/domain-data/report-plans";
import { PLAN_ID, type SEOReportPlanId } from "@/lib/seo-intents";

/**
 * The governed answer-continuation deliverables triplet for a plan (rule 40 —
 * the single source is Supabase catalog.report_deliverables → domain-data; never
 * re-authored in a .tsx). Falls back to the Brihat full-chart triplet for an
 * unknown id so a caller always has 3 rows to render.
 */
export function reportDeliverablesFor(planId: PremiumReportPlanId): ReportDeliverables {
  return (
    reportPlanOfferDetailsMap[planId]?.deliverables ??
    reportPlanOfferDetailsMap[PLAN_ID.brihat_kundali].deliverables
  );
}

type ReportCopyLanguage = "en" | "hi";

export type ReportOfferCopy = {
  priceInr: number;
  headline: string;
  subhead: string;
};

function normalizedReportCopyLanguage(language?: string | null): ReportCopyLanguage {
  return language === "hi" ? "hi" : "en";
}

function reportPriceInr(planId: SEOReportPlanId): number {
  return reportPlanSummaryMap[planId].price_inr;
}

const REPORT_COPY: Record<SEOReportPlanId, ReportOfferCopy> = {
  [PLAN_ID.brihat_kundali]: {
    priceInr: reportPriceInr(PLAN_ID.brihat_kundali),
    headline: "See why this pattern keeps repeating",
    subhead:
      "A paid kundali report with the house, dasha, dosha, and upay behind your current phase.",
  },
  [PLAN_ID.marriage_report]: {
    priceInr: reportPriceInr(PLAN_ID.marriage_report),
    headline: "Why do relationships repeat the same pattern?",
    subhead:
      "Read the 7th house, Shukra, Mangal Dosh, compatibility pattern, timing, and upay in one focused report.",
  },
  [PLAN_ID.career_report]: {
    priceInr: reportPriceInr(PLAN_ID.career_report),
    headline: "Why does hard work not turn into results?",
    subhead:
      "See your 10th house, Shani pattern, active dasha, blocked potential, and better work windows.",
  },
  [PLAN_ID.annual_forecast]: {
    priceInr: reportPriceInr(PLAN_ID.annual_forecast),
    headline: "Why does this year feel different already?",
    subhead:
      "Map the next 12 months through your dasha, transits, caution periods, decision windows, and upay.",
  },
  [PLAN_ID.sade_sati]: {
    priceInr: reportPriceInr(PLAN_ID.sade_sati),
    headline: "Why is Shani making this phase feel heavy?",
    subhead:
      "Read your Sade Sati phase, natal Shani, dasha context, pressure areas, and safe Shani upay.",
  },
  [PLAN_ID.pitra_dosh]: {
    priceInr: reportPriceInr(PLAN_ID.pitra_dosh),
    headline: "Is ancestral karma affecting your path?",
    subhead:
      "Confirm Pitra Dosh, cancellation factors, activation windows, and the exact upay sequence for your chart.",
  },
  [PLAN_ID.mahadasha_forecast]: {
    priceInr: reportPriceInr(PLAN_ID.mahadasha_forecast),
    headline: "Which mahadasha is deciding your timing?",
    subhead: "Map your current mahadasha, antardasha, next transitions, and month-level decisions.",
  },
  [PLAN_ID.muhurat_report]: {
    priceInr: reportPriceInr(PLAN_ID.muhurat_report),
    headline: "Why does the right time depend on your chart?",
    subhead:
      "Get purpose-specific muhurat guidance, avoid windows, sankalp, and ritual checklist from your kundali.",
  },
  [PLAN_ID.dosha_remedy_plan]: {
    priceInr: reportPriceInr(PLAN_ID.dosha_remedy_plan),
    headline: "Why choose an upay before confirming the dosha?",
    subhead:
      "Confirm dosha strength, cancellation factors, safe upay, mantra routine, and matched remedy products.",
  },
  [PLAN_ID.numerology_report]: {
    priceInr: reportPriceInr(PLAN_ID.numerology_report),
    headline: "Why do your numbers keep pulling the same delays?",
    subhead:
      "Read your mulank, bhagyank, the graha each one rules, name-number harmony, lucky numbers, and the upay.",
  },
  [PLAN_ID.gemstone_report]: {
    priceInr: reportPriceInr(PLAN_ID.gemstone_report),
    headline: "Why did the gemstone you were told to wear change nothing?",
    subhead:
      "Starts with the free upay for your chart, then which ratna (if any) suits your lagna and dasha, with rudraksha as an optional traditional mention. Consult an astrologer before wearing any gemstone.",
  },
  [PLAN_ID.baby_name_report]: {
    priceInr: reportPriceInr(PLAN_ID.baby_name_report),
    headline: "Which first sound does your baby's nakshatra actually ask for?",
    subhead:
      "Janma nakshatra and pada, the auspicious starting syllables, and 30+ curated names with meanings.",
  },
  [PLAN_ID.lal_kitab_report]: {
    priceInr: reportPriceInr(PLAN_ID.lal_kitab_report),
    headline: "Why do the same debts and blocks keep returning?",
    subhead:
      "Your Lal Kitab chart, ancestral rin, blind and sleeping planets, and the low-cost totke matched to them.",
  },
  [PLAN_ID.transit_forecast]: {
    priceInr: reportPriceInr(PLAN_ID.transit_forecast),
    headline: "Why does the timing never seem to be on your side?",
    subhead:
      "Saturn and Jupiter gochar over your kundali — key ingress dates, the houses they activate, and a 12-month map.",
  },
};

const HINDI_REPORT_COPY: Record<SEOReportPlanId, ReportOfferCopy> = {
  [PLAN_ID.brihat_kundali]: {
    priceInr: REPORT_COPY.brihat_kundali.priceInr,
    headline: "बृहत कुंडली रिपोर्ट में अपना पैटर्न समझें",
    subhead: "12 भाव, महादशा, दोष और उपाय एक आसान PDF में पाएँ, आपके चार्ट के सटीक ग्रहों के साथ।",
  },
  [PLAN_ID.marriage_report]: {
    priceInr: REPORT_COPY.marriage_report.priceInr,
    headline: "कुंडली मिलान रिपोर्ट से रिश्ते का पैटर्न समझें",
    subhead:
      "7वाँ भाव, शुक्र, मंगल दोष, गुण मिलान, समय और उपाय एक PDF में पाएँ, जिसे परिवार के साथ साझा कर सकें।",
  },
  [PLAN_ID.career_report]: {
    priceInr: REPORT_COPY.career_report.priceInr,
    headline: "करियर रिपोर्ट में शनि का पैटर्न देखें",
    subhead: "10वाँ भाव, चल रही दशा, रुकी हुई क्षमता और काम के बेहतर समय को चार्ट के साथ समझें।",
  },
  [PLAN_ID.annual_forecast]: {
    priceInr: REPORT_COPY.annual_forecast.priceInr,
    headline: "वार्षिक भविष्यफल में 12 महीनों का समय देखें",
    subhead: "दशा, गोचर, सावधानी के समय, फ़ैसलों के सही समय और उपाय महीने-दर-महीने PDF में पाएँ।",
  },
  [PLAN_ID.sade_sati]: {
    priceInr: REPORT_COPY.sade_sati.priceInr,
    headline: "साढ़े साती रिपोर्ट में शनि का दबाव समझें",
    subhead:
      "चंद्र राशि का चरण, जन्म कुंडली का शनि, दशा, दबाव वाले क्षेत्र और सुरक्षित शनि उपाय चार्ट से पढ़ें।",
  },
  [PLAN_ID.pitra_dosh]: {
    priceInr: REPORT_COPY.pitra_dosh.priceInr,
    headline: "पितृ दोष रिपोर्ट से पूर्वजों से जुड़ा पैटर्न पक्का करें",
    subhead:
      "9वाँ भाव, सूर्य, राहु/केतु, दोष भंग, दशा का असर और सुरक्षित पितृ उपाय एक PDF में पाएँ।",
  },
  [PLAN_ID.mahadasha_forecast]: {
    priceInr: REPORT_COPY.mahadasha_forecast.priceInr,
    headline: "महादशा भविष्यफल में अपना समय पहचानें",
    subhead: "चल रही महादशा, अंतर्दशा, अगला बदलाव और हर महीने के फ़ैसले चार्ट से समझें।",
  },
  [PLAN_ID.muhurat_report]: {
    priceInr: REPORT_COPY.muhurat_report.priceInr,
    headline: "मुहूर्त रिपोर्ट में अपना निजी शुभ समय पाएँ",
    subhead:
      "उद्देश्य, कुंडली, दशा का दबाव, टालने वाले समय, संकल्प और पूजा की सूची एक योजना में पाएँ।",
  },
  [PLAN_ID.dosha_remedy_plan]: {
    priceInr: REPORT_COPY.dosha_remedy_plan.priceInr,
    headline: "दोष उपाय योजना से सही उपाय चुनें",
    subhead:
      "दोष की ताक़त, दोष भंग, सुरक्षित उपाय, मंत्र जप का क्रम और मेल खाते उपाय चार्ट से पक्के करें।",
  },
  [PLAN_ID.numerology_report]: {
    priceInr: REPORT_COPY.numerology_report.priceInr,
    headline: "अंक ज्योतिष जीवन रिपोर्ट में अपने अंक का ग्रह समझें",
    subhead:
      "मूलांक, भाग्यांक, उनके ग्रह, नाम-अंक का तालमेल, शुभ अंक और उपाय एक आसान PDF में पाएँ।",
  },
  [PLAN_ID.gemstone_report]: {
    priceInr: REPORT_COPY.gemstone_report.priceInr,
    headline: "रुद्राक्ष और रत्न रिपोर्ट में सही रत्न जानें",
    subhead:
      "पहले आपके चार्ट का मुफ़्त उपाय, फिर लग्न और दशा के हिसाब से रत्न (अगर अनुकूल हो) — रुद्राक्ष एक वैकल्पिक पारंपरिक उल्लेख है। कोई भी रत्न पहनने से पहले ज्योतिषी से सलाह ज़रूर लें।",
  },
  [PLAN_ID.baby_name_report]: {
    priceInr: REPORT_COPY.baby_name_report.priceInr,
    headline: "शिशु नाम रिपोर्ट में नक्षत्र के शुभ अक्षर पाएँ",
    subhead: "जन्म नक्षत्र और पद, शुभ शुरुआती अक्षर, और अर्थ के साथ 30+ चुने हुए नाम।",
  },
  [PLAN_ID.lal_kitab_report]: {
    priceInr: REPORT_COPY.lal_kitab_report.priceInr,
    headline: "लाल किताब रिपोर्ट में ऋण और टोटके समझें",
    subhead:
      "आपकी लाल किताब कुंडली, पितृ ऋण, सोए और अंधे ग्रह, और उनसे मेल खाते सस्ते टोटके एक PDF में पाएँ।",
  },
  [PLAN_ID.transit_forecast]: {
    priceInr: REPORT_COPY.transit_forecast.priceInr,
    headline: "गोचर भविष्यफल में शनि-गुरु का समय देखें",
    subhead:
      "आपकी कुंडली पर शनि और गुरु का गोचर — राशि प्रवेश की तिथियाँ, सक्रिय भाव और 12 महीनों का समय।",
  },
};

export function reportOfferCopyFor(
  planId: SEOReportPlanId,
  language?: string | null
): ReportOfferCopy {
  const copyTable =
    normalizedReportCopyLanguage(language) === "hi" ? HINDI_REPORT_COPY : REPORT_COPY;
  return copyTable[planId] ?? REPORT_COPY.brihat_kundali;
}
